import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { CustomRubric } from '../../types';
import { HelpTooltip } from '../ui/HelpTooltip';

interface CustomReviewButtonProps {
  rubrics: CustomRubric[];
  activeRubricId: string | null;
  activeRubric: CustomRubric | null;
  onToggle: (rubricId: string | null) => void;
  onOpenWizard: () => void;
  /** Whether to show the help questionmark icon */
  showHelp?: boolean;
  /** Help text to display */
  helpText?: string;
  /** Callback when help is viewed */
  onHelpViewed?: () => void;
}

export const CustomReviewButton: React.FC<CustomReviewButtonProps> = ({
  rubrics,
  activeRubricId,
  activeRubric,
  onToggle,
  onOpenWizard,
  showHelp = false,
  helpText = '',
  onHelpViewed,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const isActive = activeRubricId !== null;
  const hasRubrics = rubrics.length > 0;

  const handleClick = () => {
    if (isActive) {
      // Toggle off - clear active rubric
      onToggle(null);
    } else if (hasRubrics) {
      // Has rubrics - show dropdown
      setShowDropdown(!showDropdown);
    } else {
      // No rubrics - open wizard to create first one
      onOpenWizard();
    }
  };

  const handleSelectRubric = (rubricId: string) => {
    onToggle(rubricId);
    setShowDropdown(false);
  };

  const handleCreateNew = () => {
    setShowDropdown(false);
    onOpenWizard();
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className={`inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
          transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
          ${isActive ? 'text-white border border-white/70 dark:border-white/70' : 'text-gray-400 border border-transparent'}`}
        title={
          isActive 
            ? `Custom review active: ${activeRubric?.name || 'Unknown'}. Click to disable.`
            : hasRubrics 
              ? 'Select or create a custom review rubric'
              : 'Create a custom review rubric'
        }
      >
        {isActive && activeRubric ? activeRubric.name : 'custom review'}
        {hasRubrics && !isActive && (
          <ChevronDown className="h-3 w-3 ml-0.5" />
        )}
        {showHelp && helpText && (
          <HelpTooltip
            helpText={helpText}
            onHelpViewed={onHelpViewed}
            size="sm"
          />
        )}
      </button>

      {/* Dropdown menu for selecting rubrics */}
      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-48 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <button
              onClick={handleCreateNew}
              className="w-full text-left px-3 py-2 text-xs rounded-md hover:bg-muted text-primary font-medium transition-colors"
            >
              + Create new rubric...
            </button>
          </div>
          {rubrics.length > 0 && (
            <div className="max-h-48 overflow-y-auto p-2">
              {rubrics.map((rubric) => (
                <button
                  key={rubric.id}
                  onClick={() => handleSelectRubric(rubric.id)}
                  className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${
                    activeRubricId === rubric.id
                      ? 'bg-primary/20 text-primary'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <div className="font-medium">{rubric.name}</div>
                  <div className="text-muted-foreground text-[10px] mt-0.5">
                    {rubric.inputType === 'both' ? 'Excellence & Failure' : 
                     rubric.inputType === 'excellence' ? 'Excellence-based' : 'Failure-based'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

